import React, { useEffect, useMemo, useState } from 'react';
import CRMHeader from '../../components/crm/CRMHeader';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import activitiesService, { Activity } from '../../services/crm/activitiesService';
import { toast } from 'react-hot-toast';

const MyTasks: React.FC = () => {
  const { activeOrgId } = useCrmOrgStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [items, setItems] = useState<Activity[]>([]);
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<number | null>(null);

  // Filters
  const [showCompleted, setShowCompleted] = useState(false);
  const [dueFilter, setDueFilter] = useState<'all' | 'overdue' | 'today' | 'upcoming'>('all');

  useEffect(() => { document.title = 'CRM | My Tasks'; }, []);

  const load = async () => {
    if (!activeOrgId) return;
    setLoading(true); setError(null);
    try {
      // Backend list doesn't filter by type; fetch a chunk and filter client-side
      const resp = await activitiesService.list({ organization_id: activeOrgId, limit, offset: page * limit });
      const all = (resp.data || []) as Activity[];
      const tasks = all.filter(a => a.type === 'task');
      setItems(tasks);
      setTotal(resp.count ?? null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load tasks');
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeOrgId, page, limit]);

  const filtered = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return items.filter(a => {
      if (!showCompleted && a.completed_at) return false;
      if (dueFilter === 'all') return true;
      if (!a.due_at) return false;
      const due = new Date(a.due_at);
      if (dueFilter === 'overdue') return due < startOfToday && !a.completed_at;
      if (dueFilter === 'today') return due >= startOfToday && due <= endOfToday;
      if (dueFilter === 'upcoming') return due > endOfToday && !a.completed_at;
      return true;
    });
  }, [items, showCompleted, dueFilter]);

  const toggleComplete = async (task: Activity) => {
    if (!activeOrgId) return;
    try {
      await activitiesService.update(task.id, {
        organization_id: activeOrgId,
        completed_at: task.completed_at ? undefined : new Date().toISOString()
      });
      toast.success(task.completed_at ? 'Marked incomplete' : 'Task completed');
      await load();
    } catch (e: any) {
      toast.error('Failed to update task');
    }
  };

  const setQuickDue = async (task: Activity, which: 'today' | 'tomorrow' | 'nextweek') => {
    if (!activeOrgId) return;
    const now = new Date();
    let due = new Date(now);
    if (which === 'today') {
      // keep now time
    } else if (which === 'tomorrow') {
      due.setDate(due.getDate() + 1);
    } else if (which === 'nextweek') {
      due.setDate(due.getDate() + 7);
    }
    try {
      await activitiesService.update(task.id, { organization_id: activeOrgId, due_at: due.toISOString() });
      toast.success('Due date updated');
      await load();
    } catch (e: any) {
      toast.error('Failed to update due date');
    }
  };

  return (
    <div className="space-y-4">
      <CRMHeader />
      <h1 className="text-2xl font-semibold">My Tasks</h1>
      <div className="bg-white p-4 rounded shadow space-y-3">
        {!activeOrgId && (
          <div className="text-sm text-gray-600">Select or create an organization above to begin.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <select className="border rounded p-2" value={dueFilter} onChange={e => { setDueFilter(e.target.value as any); setPage(0); }}>
            <option value="all">All</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due Today</option>
            <option value="upcoming">Upcoming</option>
          </select>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
            Show Completed
          </label>
          <select className="border rounded p-2" value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(0); }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <div className="flex items-center gap-2">
            <button className="border rounded px-3 py-2" disabled={page === 0 || loading} onClick={() => { setPage(p => Math.max(0, p - 1)); }}>&larr; Prev</button>
            <button className="border rounded px-3 py-2" disabled={total != null ? (page + 1) * limit >= total : loading} onClick={() => { setPage(p => p + 1); }}>
              Next &rarr;
            </button>
          </div>
          <button className="bg-blue-600 text-white rounded px-4" onClick={load} disabled={loading}>Load</button>
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className="min-w-full border text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-2 text-left">Task</th>
                <th className="p-2 text-left">Due</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-t">
                  <td className="p-2">{t.content || '(No title)'}</td>
                  <td className="p-2">{t.due_at ? new Date(t.due_at).toLocaleString() : '—'}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <span>{t.completed_at ? 'Completed' : 'Open'}</span>
                      <button className="text-xs border rounded px-2 py-1" onClick={() => setQuickDue(t, 'today')}>Today</button>
                      <button className="text-xs border rounded px-2 py-1" onClick={() => setQuickDue(t, 'tomorrow')}>Tomorrow</button>
                      <button className="text-xs border rounded px-2 py-1" onClick={() => setQuickDue(t, 'nextweek')}>Next Week</button>
                    </div>
                  </td>
                  <td className="p-2 text-right">
                    <button className="text-blue-600" onClick={() => toggleComplete(t)}>{t.completed_at ? 'Mark Incomplete' : 'Complete'}</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td className="p-2 text-gray-500" colSpan={4}>No tasks found.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MyTasks;
